import { safe, secondaryOf } from '../utils/contrast.js';
import { atributoDeBoton } from '../utils/boton-estilo.js';
import { fondosDelTitular, pesoMarcaDelTitular } from '../utils/foto-fondo.js';
import { tituloDePortada } from '../utils/titulo-estilo.js';
import { parseVideoUrl, renderVideoBackground, renderVideoCard } from '../utils/videoEmbed.js';
import { ctaAnchorAttrs } from '../utils/links.js';
import { conSecundario } from '../utils/boton-secundario.js';
import { altAttr } from '../utils/alt-text.js';
import { dimensionesDe, atributosDimension, asasCaja, asaTexto, cajaUnicaDe, atributosCajaUnica, asasCajaUnica } from '../utils/dimension.js';
import { escapeHtml } from '../utils/dom.js';
import { admiteRecorte, clasesImagenRecorte, claseMarcoRecorte } from '../utils/imagen-recorte.js';
import { atributosEncuadre } from '../utils/encuadre.js';

export const heroBlock = (rawProps) => {
    // El 2º argumento NO es decorativo: es lo que le dice a `safe()` que en v1/v2 el fondo es la
    // FOTO y no `bgColor`, así que el titular se contrasta contra el velo compuesto sobre ella
    // (utils/foto-fondo.js). Sin él, cada render devolvía el texto oscuro sobre la foto oscura
    // que el saneador del servidor acababa de corregir.
    const p = safe(rawProps, 'hero');
    const isSplit = p.variant === 'v3';
    const videoUrl = p.video_url || '';
    const isVideo = !!videoUrl && videoUrl !== 'placeholder';
    // Redimensionado manual de la FILA: SOLO la v3 (split) con media. Las demás llevan la foto como
    // FONDO de pantalla completa — no hay columna que estrechar, pero sí la CAJA DE TEXTO ÚNICA
    // (rama FONDO, más abajo). Las dos listas viven en utils/dimension.js.
    const dim = (isSplit && (p.use_image || isVideo)) ? dimensionesDe('hero', 'v3') : null;
    const dimAttrs = atributosDimension(p, dim);   // {clases, estilo}, una sola pasada
    // CONTRATO DE MARKUP DEL MOTOR DE DIMENSIÓN (ver utils/dimension.js): aquí la FILA es el
    // propio `<section>` (sus dos columnas son hijas directas), la CAJA DE LA IMAGEN es
    // `.hero-media-wrapper` —la que el CSS dimensiona y la que recorta— y la del texto envuelve
    // titular + subtítulo + CTA. El hero v3 pone el texto a la IZQUIERDA y la foto a la derecha,
    // así que el borde interior (el que da a la vecina) es el ESTE para el texto y el OESTE para
    // la imagen. Las clases y las variables solo salen si hay prop.

    const placeholder = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

    let mediaHtml = "";
    if (isVideo) {
        mediaHtml = renderVideoCard(videoUrl);
    } else if (p.use_image) {
        const query = p.image_url || p.image_keyword || "";
        // LCP: si la imagen YA está resuelta (URL http/absoluta → estado publicado), hornear el
        // src REAL con fetchpriority=high y opacity:1 → el navegador la pide al parsear el HTML,
        // sin esperar al JS (unsplash.js la re-hidrata igual: es idempotente). Si es una keyword
        // sin resolver (editor recién añadido), placeholder 1x1 + opacity:0 y el JS la resuelve.
        const resolved = query.startsWith("http") || query.startsWith("/");
        const imgStyle = "width: 100%; border-radius: var(--card-radius, 30px); box-shadow: var(--shadow-card, 0 30px 60px rgba(0,0,0,0.2)); object-fit: cover; aspect-ratio: 16/9; transition: opacity 0.5s;";
        // La imagen del hero es el LCP: sin `alt` se publicaba la imagen MÁS importante
        // del sitio sin texto alternativo. Se DERIVA (utils/alt-text.js): campo manual
        // → titular → subtítulo → keyword de la búsqueda. Nada hardcodeado.
        const alt = altAttr(p.image_alt, p.title, p.subtitle, p.image_keyword);
        // Escapada: es la misma keyword/URL que la rama de fondo (ver `bgData` abajo), y aquí va a
        // `src` y `data-unsplash`. Sin escapar, un `"` en ella cerraba el atributo y colaba un
        // `onload` en el editor (mismo origen que el ERP) y en la SPA pública (revisor del P2).
        const q = escapeHtml(query);
        // Estilo «Recorte» (utils/imagen-recorte.js): solo en la v3, la única con la foto en una caja.
        const recorte = admiteRecorte('hero', p) ? clasesImagenRecorte(p) : '';
        // Y su encuadre (utils/encuadre.js): foco + zoom, también solo en la v3.
        const enc = admiteRecorte('hero', p) ? atributosEncuadre(p) : { clase: '', estilo: '' };
        mediaHtml = resolved
            ? `<img src="${q}" ${alt} data-unsplash="${q}" class="unsplash-target${recorte}${enc.clase}" fetchpriority="high" decoding="async" style="${imgStyle} opacity: 1;${enc.estilo}">`
            : `<img src="${placeholder}" ${alt} data-unsplash="${q}" class="unsplash-target${recorte}${enc.clase}" style="${imgStyle} opacity: 0;${enc.estilo}">`;
    }

    // ESTILO DEL TITULAR (F6, utils/titulo-estilo.js): `titulo_estilo` decide si va en degradado (sin prop, el
    // de siempre: acento distinto del texto), el degradado a mano añade `n3i-tit-mano` y sus colores, y las
    // palabras resaltadas sus variables, medidas contra el MISMO fondo que el texto. Sin nada de eso, `estilo`
    // sale vacío y el <h1> es el de antes, byte a byte.
    const fondosTit = fondosDelTitular(p, 'hero');
    const tit = tituloDePortada(rawProps, p, fondosTit, p.accentColor && p.accentColor !== p.textColor);
    const hasGradient = tit.degradado;
    const h1Class = hasGradient ? (tit.mano ? 'title-gradient n3i-tit-mano' : 'title-gradient') : '';
    // El FINAL del degradado (la marca mezclada con el texto, ver `.title-gradient` en site.css) se
    // mide contra lo que el titular tiene DETRÁS, el mismo fondo que el texto: si con el 62 % de
    // siempre no llega a 3:1, `--title-mix` rebaja el peso de la marca lo justo (utils/foto-fondo.js).
    // La marca es la del CSS, `var(--secondary, var(--accent))`: el `--secondary` lo inyecta
    // render.js con este mismo `secondaryOf`. El que ya llega no emite nada (HTML idéntico).
    // Con un final elegido a mano (`--tit-hasta`) no hay final automático que medir: se respeta (D1).
    const pesoMarca = hasGradient && tit.estilo.indexOf('--tit-hasta:') === -1
        ? pesoMarcaDelTitular(p.textColor, secondaryOf(rawProps) || p.accentColor, fondosDelTitular(p, 'hero'))
        : null;
    // `--display-weight` (escala tipográfica como token) con el peso actual como
    // fallback → sin token, idéntico. Ver la tabla de presets en css/site.css.
    const h1Style = `font-weight: var(--display-weight, 900); line-height: 1.1; margin-bottom: 25px;`
        + (pesoMarca === null ? '' : ` --title-mix: ${pesoMarca}%;`)
        + (tit.estilo ? ` ${tit.estilo}` : '');
    // El titular del hero es EL elemento de la página: además de la escala global
    // (--type-scale) se ajusta por lo que MIDE (--title-adj, que deriva del
    // --title-len que emite el render por sección). Con los tokens ausentes ambos
    // factores valen 1 → el clamp() resuelve exactamente igual que antes.
    const h1Fit = (clamp) => `calc(${clamp} * var(--type-scale, 1) * var(--title-adj, 1))`;

    // Botón flotante para editar el video — fondo oscuro sólido (legible sobre cualquier fondo)
    const videoSvg = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>`;
    const videoPlaceholderBtn = `<div class="video-hero-placeholder editor-only" style="position:absolute;bottom:24px;left:50%;transform:translateX(-50%);z-index:3;display:flex;align-items:center;gap:8px;background:rgba(15,15,20,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.12);border-radius:30px;padding:10px 20px;color:#e2e8f0;font-size:0.8rem;font-weight:600;cursor:pointer;white-space:nowrap;" title="Configurar video">${videoSvg} ${isVideo ? 'Cambiar video' : 'Añadir video'}</div>`;

    // `--ghost-len` = nº de caracteres: el CSS dimensiona el ghost para que QUEPA (va con
    // white-space:nowrap, así que sin esto un texto largo se sale de la sección). Mismo
    // patrón que --title-len. Viaja en el HTML del bloque → paridad automática con
    // compileSiteHtml (re-renderiza desde el mismo registry), sin gotcha nº12.
    const ghostTexto = p.ghost_text ? String(p.ghost_text).substring(0, 15) : '';
    const ghostTextHtml = ghostTexto
        ? `<span class="ghost-text" aria-hidden="true" style="--ghost-len:${ghostTexto.trim().length || 1}">${ghostTexto}</span>`
        : '';

    // Variante SPLIT (v3): texto a la izquierda + media a la derecha
    if (isSplit && (p.use_image || isVideo)) {
        return `
    <section id="bloque-hero" class="reveal hero-section variant-${p.variant || 'v1'} hero-layout${dim ? ' n3i-fila' + dimAttrs.clases : ''}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; --accent-contrast:${p.accentContrast};${dimAttrs.estilo}">
        <div class="ambient-glow-layer"></div>
        ${ghostTextHtml}
        <style>
          @media (max-width: 992px) {
              .hero-text-side { text-align: center !important; }
              .hero-text-side .btn-action { margin: 0 auto; display: block; }
              .hero-image-side { width: 100% !important; margin: 0 auto !important; }
          }
        </style>
        <div class="hero-text-side${dim ? ' n3i-texto' : ''}"${dim ? ` data-interior="e"` : ''} style="flex: var(--text-flex, 1) 1 0; min-width: min(100%, 320px); position: relative;">
            ${dim ? '<div class="n3i-caja-texto">' : ''}
            <h1 data-field="title" contenteditable="true" class="${h1Class}" style="${h1Style} font-size: ${h1Fit('clamp(2.5rem, 5cqw, 4rem)')};">${p.title}</h1>
            <p data-field="subtitle" contenteditable="true" style="margin: 30px 0; font-size: calc(1.2rem * var(--text-scale, 1)); opacity: 0.92;">${p.subtitle}</p>
            ${conSecundario(`<a ${ctaAnchorAttrs(p.cta_url)} class="btn-action" data-field="cta" contenteditable="true"${atributoDeBoton(p, 'cta')}>${p.cta}</a>`, p)}
            ${dim ? asaTexto('e') + '</div>' : ''}
        </div>
        <div class="hero-image-side${dim ? ' n3i-media' : ''}"${dim ? ` data-interior="w"` : ''} style="flex: var(--media-flex, 1) 1 0; min-width: min(100%, 320px); position: relative; display: flex; justify-content: center; align-items: center;">
            <svg width="0" height="0" style="position: absolute;">
                <defs>
                    <clipPath id="hero-reveal-clip" clipPathUnits="objectBoundingBox">
                        <path class="hero-mask-path" d="M 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 Z"></path>
                    </clipPath>
                </defs>
            </svg>
            <div class="hero-media-wrapper${dim ? ' n3i-caja' : ''}${admiteRecorte('hero', p) ? claseMarcoRecorte(p) : ''}" style="position: relative; width: 100%; border-radius: var(--card-radius, 30px); overflow: hidden; box-shadow: var(--shadow-card, 0 30px 60px rgba(0,0,0,0.15)); transform-style: preserve-3d; perspective: 1000px; clip-path: url(#hero-reveal-clip);">
                ${mediaHtml}
                ${dim ? asasCaja(dim, 'w') : ''}
            </div>
        </div>
        ${videoPlaceholderBtn}
    </section>`;
    }

    // Variante FONDO (v1/v2): video o imagen como fondo de pantalla completa.
    // La keyword de la foto la escribe el LLM o el cliente y va DENTRO del tag de apertura de la
    // <section>: sin escapar, un `>` en ella («playa > sol») cerraba el tag antes de tiempo para
    // las inyecciones de render.js/compileSiteHtml y el `style` del hero salía como TEXTO visible
    // (L11 del plan del motor de fondos). Lo mismo el JSON del velo, que además iba entre comillas
    // simples: un `'` en un stop lo cortaba. `getAttribute` los devuelve tal cual.
    let bgData = p.use_image && !isVideo ? `data-unsplash-bg="${escapeHtml(p.image_url || p.image_keyword)}" data-bg-color="${p.bgColor}"` : "";
    // Gradient independiente de imagen — funciona con bg-animation sin imagen
    if (p.bg_gradient) {
        bgData += ` data-bg-gradient="${escapeHtml(JSON.stringify(p.bg_gradient))}"`;
    }
    const videoBg = isVideo ? `
    ${renderVideoBackground(videoUrl)}
    <div style="position:absolute;inset:0;background:linear-gradient(180deg, ${p.bgColor}00 0%, ${p.bgColor}dd 100%);z-index:1;"></div>` : "";

    // CAJA DE TEXTO ÚNICA (utils/dimension.js): la caja de 850 px se ensancha o estrecha con sus
    // asas. Clase, variable y clase de la caja SOLO con prop; las asas van pegadas a la CTA, sin
    // espacio, para que `compileSiteHtml` las borre sin dejar rastro (publicado idéntico sin prop).
    const fichaU = cajaUnicaDe('hero', p.variant || 'v1');
    const u = atributosCajaUnica(p, fichaU);
    return `
  <section id="bloque-hero" class="reveal hero-section unsplash-bg-target variant-${p.variant || 'v1'} hero-layout-centered${u.clases}" ${bgData} style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; --accent-contrast:${p.accentContrast}; text-align: ${p.variant === 'v2' ? 'center' : 'left'};${u.estilo}">
    <div class="ambient-glow-layer"></div>
    ${ghostTextHtml}
    ${videoBg}
    <div${u.claseCaja ? ` class="${u.claseCaja}"` : ''} style="max-width: 850px; z-index: 2; position: relative;">
        <h1 data-field="title" contenteditable="true" class="${h1Class}" style="${h1Style} font-size: ${h1Fit('clamp(2.5rem, 6cqw, 5rem)')};">${p.title}</h1>
        <p data-field="subtitle" contenteditable="true" style="margin: 30px 0; font-size: calc(1.4rem * var(--text-scale, 1)); opacity: 0.94;">${p.subtitle}</p>
        ${conSecundario(`<a ${ctaAnchorAttrs(p.cta_url)} class="btn-action" data-field="cta" contenteditable="true"${atributoDeBoton(p, 'cta')}>${p.cta}</a>`, p)}${asasCajaUnica(fichaU)}
    </div>
    ${videoPlaceholderBtn}
  </section>`;
}
